import { usePostLoginMutation } from '@/api/api.slice';
import { useAppDispatch } from '@/app/hooks';
import {
  Alert,
  Button,
  Link,
  MotionFadeIn,
  TextField,
  TextFieldPassword,
} from '@arsnovasystems/ars-front-lib';
import { Box, Stack } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';

import { setIsLogged } from '@/app/Application.slice.ts';
import { LocalStorage } from '@/services/LocalStorage.ts';

import i18n from '@/i18n/i18n.ts';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface LoginForm {
  email: string;
  password: string;
}

export const SPECIFIC_PARTNER_BG_BANNER = '#002366';

export const Login = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { control, handleSubmit } = useForm<LoginForm>({
    defaultValues: {
      email: LocalStorage.getEmail() ?? '',
      password: '',
    },
  });

  const [loginUser, loginResult] = usePostLoginMutation();

  const lostCredentials = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void navigate('/lost-credentials');
  };

  const onSubmit = async (data: LoginForm) => {
    const formData = {
      password: data.password,
      email: data.email,
      lang: i18n.language,
    };

    //send query to connect
    const result = await loginUser(formData);
    if (result.error === undefined) {
      dispatch(setIsLogged(true));
      void navigate('/');
    }
  };

  return (
    <Box
      component={'form'}
      sx={{
        width: '100%',
      }}
      onSubmit={(event) => void handleSubmit(onSubmit)(event)}
    >
      <Stack
        sx={{
          gap: 2,
          width: '100%',
          marginTop: 5,
        }}
      >
        <Controller
          name='email'
          control={control}
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <TextField
              autoComplete={'email'}
              label={t('ik.email')}
              fullWidth={true}
              error={fieldState.invalid}
              {...field}
            />
          )}
        />
        <Controller
          name='password'
          control={control}
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <Stack sx={{ alignItems: 'flex-end' }}>
              <TextFieldPassword
                fullWidth={true}
                error={fieldState.invalid}
                label={t('Password')}
                {...field}
              />

              <Stack
                direction={'row'}
                sx={{
                  justifyContent: 'space-between',
                  flex: 1,
                  width: '100%',
                }}
              >
                <Link
                  component='button'
                  type='button'
                  onClick={lostCredentials}
                  sx={{
                    textDecoration: 'none',
                    marginTop: 1,
                    fontWeight: 500,
                    fontSize: '1.2rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                  variant='body1'
                >
                  {t('ik.login.link.lostPassword')}
                </Link>
              </Stack>
            </Stack>
          )}
        />

        <Button
          variant='contained'
          type={'submit'}
          fullWidth
        >
          {t('ik.log.in')}
        </Button>

        {loginResult.error && (
          <MotionFadeIn>
            <Alert
              severity={'error'}
              variant={'outlined'}
            >
              {t('ik.login.error.invalidCredentials')}
            </Alert>
          </MotionFadeIn>
        )}
      </Stack>
    </Box>
  );
};
